import {
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
} from '@chakra-ui/react';
// eslint-disable-next-line no-restricted-imports
import { Field, Form, Formik } from 'formik';
import { FC, useState } from 'react';
import { Button } from 'src/components/atoms/Button';
import { ErrorNotifBox } from 'src/components/atoms/ErrorNotifBox';
import { useFontErrorColor } from 'src/hooks/colors/useErrorColor';
import { FormikField } from './types';
import { validateCode, validateEmail, validatePassword } from './validators';

type ForgotPasswordUpdateProps = {
  email?: string;
  onSubmit: (email: string, code: string, newPassword: string) => void;
};

/**
 * This component shows is required when a user needs to set a new password
 * (usually at first sign in with a new account).
 */
export const ForgotPasswordUpdate: FC<ForgotPasswordUpdateProps> = ({
  email,
  onSubmit,
}) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const errorMessageColor = useFontErrorColor();

  return (
    <Formik
      initialValues={{
        email: email || '',
        code: '',
        newPassword: '',
      }}
      onSubmit={async (values) => {
        try {
          setErrorMessage(null);
          await onSubmit(values.email, values.code, values.newPassword);
        } catch (error) {
          if (error instanceof Error) {
            setErrorMessage(error.message);
          }
        }
      }}>
      {(props) => (
        <Form>
          <Field name="email" validate={validateEmail}>
            {({ field, form }: FormikField) => (
              <FormControl
                my={4}
                isInvalid={form.errors.email && form.touched.email}>
                <FormLabel
                  htmlFor="email"
                  fontFamily="marketingPage"
                  fontSize="16px"
                  fontWeight={500}
                  color="content.title"
                  mb={3}>
                  Email
                </FormLabel>
                <Input
                  {...field}
                  id="email"
                  placeholder="Enter Email"
                  _placeholder={{ color: 'content.muted' }}
                  bg="surface.panel"
                  border="1px solid"
                  borderColor="surface.border"
                  borderRadius="md"
                  _focus={{
                    borderColor: 'icon.green',
                    boxShadow: '0 0 0 1px var(--chakra-colors-icon-green)',
                  }}
                />
                <FormErrorMessage color={errorMessageColor}>
                  {form.errors.email}
                </FormErrorMessage>
              </FormControl>
            )}
          </Field>
          <Field name="code" validate={validateCode}>
            {({ field, form }: FormikField) => (
              <FormControl
                my={4}
                isInvalid={form.errors.code && form.touched.code}>
                <FormLabel
                  htmlFor="code"
                  fontFamily="marketingPage"
                  fontSize="16px"
                  fontWeight={500}
                  color="content.title"
                  mb={3}>
                  Reset Code (Sent via email)
                </FormLabel>
                <Input
                  {...field}
                  id="code"
                  _placeholder={{ color: 'content.muted' }}
                  bg="surface.panel"
                  border="1px solid"
                  borderColor="surface.border"
                  borderRadius="md"
                  _focus={{
                    borderColor: 'icon.green',
                    boxShadow: '0 0 0 1px var(--chakra-colors-icon-green)',
                  }}
                />
                <FormErrorMessage color={errorMessageColor}>
                  {form.errors.code}
                </FormErrorMessage>
              </FormControl>
            )}
          </Field>
          <Field name="newPassword" validate={validatePassword}>
            {({ field, form }: FormikField) => (
              <FormControl
                my={4}
                isInvalid={form.errors.newPassword && form.touched.newPassword}>
                <FormLabel
                  htmlFor="newPassword"
                  fontFamily="marketingPage"
                  fontSize="16px"
                  fontWeight={500}
                  color="content.title"
                  mb={3}>
                  New Password
                </FormLabel>
                <Input
                  {...field}
                  type="password"
                  id="newPassword"
                  _placeholder={{ color: 'content.muted' }}
                  bg="surface.panel"
                  border="1px solid"
                  borderColor="surface.border"
                  borderRadius="md"
                  _focus={{
                    borderColor: 'icon.green',
                    boxShadow: '0 0 0 1px var(--chakra-colors-icon-green)',
                  }}
                />
                <FormErrorMessage color={errorMessageColor}>
                  {form.errors.newPassword}
                </FormErrorMessage>
              </FormControl>
            )}
          </Field>
          {errorMessage && <ErrorNotifBox title={errorMessage} />}
          <Flex direction="row" justifyContent="flex-end">
            <Button
              buttonName="update-password"
              mt={4}
              isLoading={props.isSubmitting}
              type="submit">
              Update Password
            </Button>
          </Flex>
        </Form>
      )}
    </Formik>
  );
};
